import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createManualQuoteItem,
  deleteManualQuoteItem,
  listManualQuoteItems,
  updateManualQuoteItem,
  type CreateManualQuoteItemBody,
  type ManualQuoteItem,
  type ManualQuoteItemList,
  type UpdateManualQuoteItemBody,
} from "../api/manualQuoteItems";

const KEY = "manual-quote-items";

/** All active manual quote items (§L — HTTP via TanStack Query). */
export function useManualQuoteItems() {
  return useQuery<ManualQuoteItemList>({
    queryKey: [KEY, "list"],
    queryFn: () => listManualQuoteItems(),
    staleTime: 30_000,
  });
}

export function useCreateManualQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation<ManualQuoteItem, Error, CreateManualQuoteItemBody>({
    mutationFn: (body) => createManualQuoteItem(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdateManualQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation<ManualQuoteItem, Error, { id: string; body: UpdateManualQuoteItemBody }>({
    mutationFn: ({ id, body }) => updateManualQuoteItem(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** Soft-delete via DELETE /v1/manual-quote-items/:id. */
export function useDeleteManualQuoteItem() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteManualQuoteItem(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
